// NEW_FILE_JUSTIFIED: Hook for employee-linked policy form state — manages policySource, company/employee picker, auto-population logic
// HOOK_STATE_JUSTIFIED: multiple useState calls for compound form state — cannot reduce below 3 without a reducer
import { useState, useCallback } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/ need two levels up
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/
import type { TPolicySource } from '@/@types/insurance';
import type { IUsePolicyFormReturn } from '@/hooks/usePolicyForm';

// LOCAL_TYPE_JUSTIFIED: employee data shape from Convex query, used only in this hook for field mapping
interface IEmployeeData {
  _id: Id<'companyEmployees'>;
  companyId: Id<'companies'>;
  orgId: Id<'organisations'>;
  name: string;
  email?: string;
  phoneNumber?: string;
  position?: string;
  postalCode?: string;
  city?: string;
  identityDocumentNumber?: string;
  nationality?: string;
  dateOfBirth?: string;
  drivingLicenseFront?: { storageId: Id<'_storage'>; fileName: string };
  drivingLicenseBack?: { storageId: Id<'_storage'>; fileName: string };
}

// Field label mapping: employee field → dynamic field label (case-insensitive match)
const EMPLOYEE_FIELD_MAP: { employeeField: keyof IEmployeeData; fieldLabels: string[] }[] = [
  { employeeField: 'email', fieldLabels: ['email address', 'email'] },
  { employeeField: 'phoneNumber', fieldLabels: ['phone number', 'phone'] },
  { employeeField: 'postalCode', fieldLabels: ['postal code', 'postcode', 'zip code'] },
  { employeeField: 'city', fieldLabels: ['city'] },
  { employeeField: 'nationality', fieldLabels: ['nationality'] },
  { employeeField: 'dateOfBirth', fieldLabels: ['date of birth', 'birthday', 'dob'] },
  {
    employeeField: 'identityDocumentNumber',
    fieldLabels: ['id number', 'passport number', 'identity document number'],
  },
];

export interface IUseEmployeePolicyFormReturn {
  policySource: TPolicySource;
  setPolicySource: (source: TPolicySource) => void;
  selectedCompanyId: string;
  setSelectedCompanyId: (id: string) => void;
  selectedEmployeeId: string;
  setSelectedEmployeeId: (id: string) => void;
  companies: { _id: Id<'companies'>; name: string; status: string }[];
  employees: { _id: string; name: string; isTeamMember: boolean; roles?: string[] }[];
  employeeData: IEmployeeData | null | undefined;
  isPrefilledFromUrl: boolean;
  showSwitchConfirm: boolean;
  pendingEmployeeId: string | null;
  handleEmployeeChange: (newEmployeeId: string, currentCustomerName: string) => void;
  confirmEmployeeSwitch: () => void;
  cancelEmployeeSwitch: () => void;
  selectedIsTeamMember: boolean;
  handlePolicySourceChange: (source: TPolicySource) => void;
  populateFormFromEmployee: (
    form: IUsePolicyFormReturn,
    driverForm: {
      drivers: { fullName: string }[];
      addDriver: () => void;
      updateDriver: (index: number, field: string, value: string) => void;
      setDriverFile: (index: number, field: string, storageId: Id<'_storage'>) => void;
    },
    supportsDrivers: boolean,
    dynamicFieldLabels: string[]
  ) => void;
  clearEmployeeData: (form: IUsePolicyFormReturn, dynamicFieldLabels: string[]) => void;
}

export function useEmployeePolicyForm(
  orgId: Id<'organisations'> | undefined,
  initialSource: TPolicySource,
  initialCompanyId?: string,
  initialEmployeeId?: string
): IUseEmployeePolicyFormReturn {
  const [policySource, setPolicySourceState] = useState<TPolicySource>(initialSource);
  const [selectedCompanyId, setSelectedCompanyId] = useState(initialCompanyId ?? '');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(initialEmployeeId ?? '');
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const [pendingEmployeeId, setPendingEmployeeId] = useState<string | null>(null);

  const isPrefilledFromUrl = Boolean(initialCompanyId && initialEmployeeId);

  // Confirmation dialog handlers
  const handleEmployeeChange = useCallback(
    (newEmployeeId: string, currentCustomerName: string) => {
      if (currentCustomerName.trim() && newEmployeeId && newEmployeeId !== selectedEmployeeId) {
        setPendingEmployeeId(newEmployeeId);
        setShowSwitchConfirm(true);
        return;
      }
      setSelectedEmployeeId(newEmployeeId);
    },
    [selectedEmployeeId]
  );

  const confirmEmployeeSwitch = useCallback(() => {
    if (pendingEmployeeId !== null) {
      setSelectedEmployeeId(pendingEmployeeId);
      setPendingEmployeeId(null);
    }
    setShowSwitchConfirm(false);
  }, [pendingEmployeeId]);

  const cancelEmployeeSwitch = useCallback(() => {
    setPendingEmployeeId(null);
    setShowSwitchConfirm(false);
  }, []);

  const handlePolicySourceChange = useCallback((source: TPolicySource) => {
    setPolicySourceState(source);
    if (source === 'personal') {
      setSelectedCompanyId('');
      setSelectedEmployeeId('');
    }
  }, []);

  // Expose setPolicySource as the internal setter for edit mode init
  const setPolicySource = setPolicySourceState;

  // Picker queries
  const companiesData = useQuery(
    api['insurance/policies'].listCompaniesForPolicyForm,
    orgId && policySource === 'company_employer' ? { orgId } : 'skip'
  );

  const employeesData = useQuery(
    api['insurance/policies'].listEmployeesForPolicyForm,
    selectedCompanyId && policySource === 'company_employer'
      ? { companyId: selectedCompanyId as Id<'companies'> } // AS_JUSTIFIED: selectedCompanyId comes from picker populated with company IDs
      : 'skip'
  );

  const companies = companiesData ?? [];
  const employees = employeesData ?? [];

  // Check if selected person is a team member (no employee record yet).
  // When the employee list hasn't loaded yet, we don't know — default to skipping the query to avoid
  // passing a userId to a validator expecting Id<'companyEmployees'>.
  const selectedEntry = employees.find(e => e._id === selectedEmployeeId);
  const selectedIsTeamMember = selectedEntry?.isTeamMember ?? false;
  const employeeListReady = employeesData !== undefined;
  const isConfirmedEmployee = employeeListReady && selectedEntry && !selectedEntry.isTeamMember;

  // Full employee data for auto-population (skip for team members and while list is loading)
  const employeeData = useQuery(
    api.companies.employees.get,
    selectedEmployeeId && policySource === 'company_employer' && isConfirmedEmployee
      ? { employeeId: selectedEmployeeId as Id<'companyEmployees'> } // AS_JUSTIFIED: selectedEmployeeId confirmed as companyEmployee from loaded list
      : 'skip'
  );

  const populateFormFromEmployee = useCallback(
    (
      form: IUsePolicyFormReturn,
      driverForm: {
        drivers: { fullName: string }[];
        addDriver: () => void;
        updateDriver: (index: number, field: string, value: string) => void;
        setDriverFile: (index: number, field: string, storageId: Id<'_storage'>) => void;
      },
      supportsDrivers: boolean,
      dynamicFieldLabels: string[]
    ) => {
      if (!employeeData) return;

      // Set customer name
      form.setCustomerName(employeeData.name);

      // Map employee fields to dynamic fields by label matching
      for (const mapping of EMPLOYEE_FIELD_MAP) {
        const empValue = employeeData[mapping.employeeField];
        if (typeof empValue !== 'string' || !empValue) continue;

        const matchingLabel = dynamicFieldLabels.find(label =>
          mapping.fieldLabels.includes(label.toLowerCase())
        );
        if (matchingLabel) {
          form.setFieldValue(matchingLabel, empValue);
        }
      }

      // For car insurance: populate first driver with employee data
      if (supportsDrivers) {
        // Ensure at least one driver exists
        if (driverForm.drivers.length === 0) {
          driverForm.addDriver();
        }

        // Set driver fields (index 0 = main driver)
        driverForm.updateDriver(0, 'fullName', employeeData.name);
        if (employeeData.dateOfBirth) {
          driverForm.updateDriver(0, 'birthday', employeeData.dateOfBirth);
        }
        if (employeeData.nationality) {
          driverForm.updateDriver(0, 'nationality', employeeData.nationality);
        }
        if (employeeData.identityDocumentNumber) {
          driverForm.updateDriver(0, 'idNumber', employeeData.identityDocumentNumber);
        }

        // Set driving license files if available
        if (employeeData.drivingLicenseFront) {
          driverForm.setDriverFile(
            0,
            'licenseFrontStorageId',
            employeeData.drivingLicenseFront.storageId
          );
        }
        if (employeeData.drivingLicenseBack) {
          driverForm.setDriverFile(
            0,
            'licenseBackStorageId',
            employeeData.drivingLicenseBack.storageId
          );
        }
      }
    },
    [employeeData]
  );

  const clearEmployeeData = useCallback(
    (form: IUsePolicyFormReturn, dynamicFieldLabels: string[]) => {
      form.setCustomerName('');

      for (const mapping of EMPLOYEE_FIELD_MAP) {
        const matchingLabel = dynamicFieldLabels.find(label =>
          mapping.fieldLabels.includes(label.toLowerCase())
        );
        if (matchingLabel) {
          form.setFieldValue(matchingLabel, '');
        }
      }
    },
    []
  );

  return {
    policySource,
    setPolicySource,
    selectedCompanyId,
    setSelectedCompanyId,
    selectedEmployeeId,
    setSelectedEmployeeId,
    companies,
    employees,
    employeeData,
    selectedIsTeamMember,
    isPrefilledFromUrl,
    showSwitchConfirm,
    pendingEmployeeId,
    handleEmployeeChange,
    confirmEmployeeSwitch,
    cancelEmployeeSwitch,
    handlePolicySourceChange,
    populateFormFromEmployee,
    clearEmployeeData,
  };
}
